import { useState } from "react";
import { useNavigate, Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { brand } from "@/lib/brand";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";
import { LogoMark } from "@/components/ui/TriIcons";
import type { AppRole } from "@/lib/auth-context";

// ── palette ──────────────────────────────────────────────────────────────────
const WINE     = "#7B1416";
const WINE_L   = "#A42022";
const CHARCOAL = "#1C1917";
const SAND     = "#C4A882";
const SAND_L   = "#D4B89A";
const CREAM    = "#F5EFE4";
const CREAM2   = "#EDE4D4";
const FD       = "'Georgia', 'Times New Roman', serif";
const FS       = "-apple-system, BlinkMacSystemFont, 'Inter', system-ui, sans-serif";

const GRAIN = `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const VALID_ROLES: AppRole[] = ["parent", "student", "school_admin", "merchant", "superadmin"];

const portalOptions: { key: AppRole; label: string; abbr: string }[] = [
  { key: "parent",       label: "Parent",        abbr: "PR" },
  { key: "student",      label: "Student",       abbr: "ST" },
  { key: "school_admin", label: "School Admin",  abbr: "SA" },
  { key: "merchant",     label: "Merchant",      abbr: "MR" },
  { key: "superadmin",   label: "Platform",      abbr: "PL" },
];

// ── flow steps ────────────────────────────────────────────────────────────────
const steps = [
  {
    icon: (
      // Megaphone-style "Pay" icon
      <svg viewBox="0 0 48 48" fill="none" className="h-12 w-12">
        <rect x="4" y="14" width="16" height="20" rx="2" fill={SAND} />
        <polygon points="20,10 44,4 44,44 20,38" fill={CHARCOAL} />
        <rect x="4" y="34" width="8" height="10" rx="1" fill={CHARCOAL} />
        <circle cx="44" cy="24" r="4" fill={SAND} />
      </svg>
    ),
    label: "CHOOSE PORTAL",
  },
  {
    icon: (
      // Target/crosshair icon
      <svg viewBox="0 0 48 48" fill="none" className="h-12 w-12">
        <circle cx="24" cy="24" r="18" stroke={CHARCOAL} strokeWidth="3" fill="none" />
        <circle cx="24" cy="24" r="9" stroke={SAND} strokeWidth="3" fill="none" />
        <circle cx="24" cy="24" r="3" fill={CHARCOAL} />
        <line x1="24" y1="4" x2="24" y2="12" stroke={CHARCOAL} strokeWidth="2.5" strokeLinecap="round" />
        <line x1="24" y1="36" x2="24" y2="44" stroke={CHARCOAL} strokeWidth="2.5" strokeLinecap="round" />
        <line x1="4" y1="24" x2="12" y2="24" stroke={CHARCOAL} strokeWidth="2.5" strokeLinecap="round" />
        <line x1="36" y1="24" x2="44" y2="24" stroke={CHARCOAL} strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    ),
    label: "SIGN IN",
  },
  {
    icon: (
      // Handshake icon
      <svg viewBox="0 0 48 48" fill="none" className="h-12 w-12">
        <path d="M4 28 L16 20 L22 24 L28 20 L44 28" stroke={CHARCOAL} strokeWidth="3" strokeLinejoin="round" fill="none" />
        <circle cx="12" cy="16" r="5" fill={SAND} />
        <circle cx="36" cy="16" r="5" fill={CHARCOAL} />
        <circle cx="24" cy="30" r="5" fill={SAND} />
        <path d="M16 20 C 20 14, 28 14, 32 20" stroke={CHARCOAL} strokeWidth="2.5" fill="none" />
      </svg>
    ),
    label: "GET ACCESS",
  },
];

export function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [role, setRole] = useState<AppRole>(() => {
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search).get("portal") as AppRole | null;
      if (p && VALID_ROLES.includes(p)) return p;
    }
    return "parent";
  });
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email, password,
          options: { data: { full_name: fullName, role } },
        });
        if (error) throw error;
        toast.success("Account created — welcome.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Welcome back.");
      }
      navigate({ to: "/app" });
    } catch (err: any) {
      toast.error(err.message ?? "Authentication failed");
    } finally {
      setLoading(false);
    }
  }

  const activePortal = portalOptions.find(p => p.key === role)!;

  return (
    <div
      className="relative flex min-h-screen overflow-hidden"
      style={{ background: CREAM, fontFamily: FS }}
    >
      {/* Global grain */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: GRAIN, backgroundSize: "200px 200px" }} />

      {/* ── LEFT EDITORIAL PANEL ─────────────────────────────────── */}
      <div
        className="relative hidden flex-col justify-between overflow-hidden lg:flex lg:w-[52%]"
        style={{ background: CHARCOAL }}
      >
        {/* Warm radial glows */}
        <div className="pointer-events-none absolute inset-0"
          style={{ background: `radial-gradient(ellipse 70% 50% at 20% 15%, ${WINE}20 0%, transparent 55%), radial-gradient(ellipse 60% 50% at 80% 85%, ${SAND}14 0%, transparent 55%)` }} />
        {/* Dot grid */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: `radial-gradient(circle, ${SAND} 1px, transparent 1px)`, backgroundSize: "32px 32px" }} />
        {/* Grain on dark */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: GRAIN, backgroundSize: "200px 200px" }} />

        {/* Top ruled band */}
        <div className="absolute top-0 left-0 right-0 h-2" style={{ background: `linear-gradient(90deg, ${WINE}, ${SAND}, ${WINE})` }} />

        {/* Gold swoosh decoration (top-right) */}
        <div className="absolute -top-8 right-0 h-48 w-32 opacity-70"
          style={{ background: `linear-gradient(135deg, ${SAND}00 0%, ${SAND}60 50%, ${SAND}00 100%)`, transform: "rotate(15deg) skewX(-10deg)", borderRadius: "0 0 60% 60%" }} />

        {/* Gold swoosh decoration (bottom-left) */}
        <div className="absolute -bottom-12 left-0 h-48 w-40 opacity-50"
          style={{ background: `linear-gradient(315deg, ${SAND}00 0%, ${SAND}50 50%, ${SAND}00 100%)`, transform: "rotate(-20deg) skewX(10deg)", borderRadius: "60% 60% 0 0" }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between h-full p-12 xl:p-16">

          {/* Brand mark */}
          <Link to="/" className="flex items-center gap-3">
            <div className="relative h-10 w-10 shrink-0">
              <div className="absolute inset-0 rounded-[11px]"
                style={{ background: `linear-gradient(135deg, ${WINE} 0%, #5C0F11 100%)` }} />
              <div className="absolute inset-0 rounded-[11px]"
                style={{ background: "radial-gradient(circle at 35% 30%, rgba(255,255,255,0.18) 0%, transparent 55%)" }} />
              <LogoMark className="absolute inset-0 m-auto h-5 w-5" accent={SAND} style={{ color: "rgba(255,255,255,0.92)" }} />
            </div>
            <div className="leading-tight">
              <div className="text-lg font-bold tracking-tight text-white" style={{ fontFamily: FD }}>{brand.name}</div>
              <div className="text-[8px] font-bold uppercase tracking-[0.25em]" style={{ color: SAND }}>{brand.tagline}</div>
            </div>
          </Link>

          {/* HERO HEADLINE */}
          <div className="my-auto">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Edition label */}
              <div className="flex items-center gap-3 mb-8">
                <div className="h-px w-10" style={{ background: SAND }} />
                <span className="text-[10px] font-bold uppercase tracking-[0.35em]" style={{ color: SAND, fontFamily: FS }}>
                  School Finance OS
                </span>
              </div>

              {/* Massive bold headline */}
              <h1 style={{
                fontFamily: FD, color: "white",
                fontSize: "clamp(3rem, 7vw, 6rem)", fontWeight: 900,
                lineHeight: 0.88, letterSpacing: "-0.04em",
              }}>
                ONE TAP.
              </h1>
              <h1 style={{
                fontFamily: FD, color: SAND,
                fontSize: "clamp(3rem, 7vw, 6rem)", fontWeight: 900,
                lineHeight: 0.88, letterSpacing: "-0.04em", fontStyle: "italic",
              }}>
                Every Bill.
              </h1>

              <p className="mt-6 max-w-sm text-base leading-relaxed" style={{ fontFamily: FD, fontStyle: "italic", color: "rgba(255,255,255,0.55)" }}>
                Pay school fees, transport, meals and uniforms in a single Mobile Money tap — automatic, instant, reconciled.
              </p>
            </motion.div>

            {/* ── 3-STEP FLOW (image-inspired) ── */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="mt-12"
            >
              <div className="flex items-center gap-4">
                {steps.map((step, i) => (
                  <div key={step.label} className="flex items-center gap-4">
                    <div className="flex flex-col items-center gap-2">
                      {/* Icon box */}
                      <div className="flex h-20 w-20 items-center justify-center"
                        style={{ background: "rgba(255,255,255,0.07)", border: `1.5px solid ${SAND}30` }}>
                        {step.icon}
                      </div>
                      <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-center"
                        style={{ color: SAND, fontFamily: FS }}>
                        {step.label}
                      </span>
                    </div>
                    {i < steps.length - 1 && (
                      <div className="flex items-center gap-1 mb-6">
                        <div className="h-px w-6" style={{ background: `${SAND}50` }} />
                        <ArrowRight className="h-4 w-4" style={{ color: `${SAND}70` }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Bottom footer line */}
          <div className="text-[11px]" style={{ color: "rgba(255,255,255,0.25)", fontFamily: FS }}>
            {brand.company} · {brand.city}, {brand.country}
          </div>
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ─────────────────────────────────────── */}
      <div className="relative flex flex-1 flex-col justify-center px-6 py-16 sm:px-10 lg:px-14 xl:px-20">

        {/* Mobile brand */}
        <div className="mb-10 flex items-center gap-3 lg:hidden">
          <div className="relative h-9 w-9 shrink-0">
            <div className="absolute inset-0 rounded-[10px]" style={{ background: `linear-gradient(135deg, ${WINE} 0%, #5C0F11 100%)` }} />
            <LogoMark className="absolute inset-0 m-auto h-5 w-5" accent={SAND} style={{ color: "rgba(255,255,255,0.92)" }} />
          </div>
          <div className="text-lg font-bold tracking-tight" style={{ fontFamily: FD, color: CHARCOAL }}>{brand.name}</div>
        </div>

        <div className="w-full max-w-[420px]">

          {/* Headline */}
          <motion.div
            key={mode}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8" style={{ background: WINE }} />
              <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: WINE, fontFamily: FS }}>
                {activePortal.label} Portal
              </span>
            </div>

            <h2 style={{ fontFamily: FD, color: CHARCOAL, fontWeight: 900, fontSize: "2.4rem", lineHeight: 0.92, letterSpacing: "-0.03em" }}>
              {mode === "signin" ? "Welcome back." : "Join the platform."}
            </h2>
            <p style={{ fontFamily: FD, fontStyle: "italic", color: WINE, fontWeight: 700, fontSize: "2.4rem", lineHeight: 0.92, letterSpacing: "-0.03em" }}>
              {mode === "signin" ? "Sign in." : "Create account."}
            </p>
          </motion.div>

          {/* ── PORTAL SELECTOR ── */}
          <div className="mt-8 flex gap-1.5 flex-wrap">
            {portalOptions.map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => setRole(p.key)}
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all"
                style={{
                  fontFamily: FS,
                  background: role === p.key ? WINE : "transparent",
                  color: role === p.key ? "white" : `${CHARCOAL}60`,
                  border: `1.5px solid ${role === p.key ? WINE : `${CHARCOAL}20`}`,
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* ── FORM ── */}
          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "signup" && (
              <AuthField
                label="Full name"
                id="name"
                type="text"
                value={fullName}
                onChange={setFullName}
                placeholder="Jane Namusoke"
                required
              />
            )}
            <AuthField
              label="Email address"
              id="email"
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="you@example.com"
              required
            />
            <div className="relative">
              <AuthField
                label="Password"
                id="password"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={setPassword}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                className="absolute right-3 top-[34px] p-1"
                style={{ color: `${CHARCOAL}50` }}
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {/* Forgot password */}
            {mode === "signin" && (
              <div className="text-right">
                <a href="/reset-password" className="text-xs font-semibold uppercase tracking-wider" style={{ color: WINE, fontFamily: FS }}>
                  Forgot password?
                </a>
              </div>
            )}

            {/* Submit button — bold black poster style */}
            <button
              type="submit"
              disabled={loading}
              className="mt-2 flex w-full items-center justify-center gap-3 py-4 text-sm font-bold uppercase tracking-[0.2em] transition-all hover:opacity-90 disabled:opacity-60"
              style={{ background: CHARCOAL, color: "white", fontFamily: FS, border: "none" }}
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Please wait…</>
              ) : (
                <>
                  {mode === "signin" ? "Let's Go" : "Create Account"}
                  <span className="flex items-center gap-1 px-3 py-1 text-xs font-bold" style={{ background: SAND, color: CHARCOAL }}>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </>
              )}
            </button>
          </form>

          {/* Divider + toggle */}
          <div className="mt-8 flex items-center gap-4">
            <div className="h-px flex-1" style={{ background: `${CHARCOAL}15` }} />
            <span className="text-xs" style={{ color: `${CHARCOAL}50`, fontFamily: FS }}>
              {mode === "signin" ? "New to TriSphere?" : "Already have an account?"}
            </span>
            <div className="h-px flex-1" style={{ background: `${CHARCOAL}15` }} />
          </div>

          <button
            type="button"
            onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
            className="mt-4 flex w-full items-center justify-center py-3.5 text-sm font-bold uppercase tracking-[0.15em] transition-all hover:opacity-80"
            style={{ background: "transparent", color: CHARCOAL, border: `1.5px solid ${CHARCOAL}25`, fontFamily: FS }}
          >
            {mode === "signin" ? "Create an Account" : "Sign In Instead"}
          </button>

          {/* Bottom trust line */}
          <p className="mt-8 text-center text-[11px] leading-relaxed" style={{ color: `${CHARCOAL}40`, fontFamily: FS }}>
            Secured by TriSphere · {brand.city}, {brand.country}<br />
            All transactions are encrypted and audit-logged
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Reusable field ─────────────────────────────────────────────────────────────
function AuthField({
  label, id, type, value, onChange, placeholder, required,
}: {
  label: string; id: string; type: string;
  value: string; onChange: (v: string) => void;
  placeholder: string; required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-1.5" style={{ color: CHARCOAL, fontFamily: FS }}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full px-4 py-3 text-sm outline-none transition-all"
        style={{
          background: CREAM2,
          border: `1.5px solid ${CHARCOAL}18`,
          color: CHARCOAL,
          fontFamily: FS,
        }}
        onFocus={e => { e.currentTarget.style.borderColor = WINE; e.currentTarget.style.background = CREAM; }}
        onBlur={e => { e.currentTarget.style.borderColor = `${CHARCOAL}18`; e.currentTarget.style.background = CREAM2; }}
      />
    </div>
  );
}
